import { INSURANCE_VERTICAL_PATHS } from './insuranceVerticals'

export enum Environment {
  PRODUCTION = 'PRODUCTION',
  STAGING = 'STAGING',
  PREVIEW = 'PREVIEW',
  DEVELOPMENT = 'DEVELOPMENT',
  UNKNOWN = 'UNKNOWN',
}

function detectEnvironment() {
  if (process.env.CAI_ENV === 'production') {
    return Environment.PRODUCTION
  }

  if (process.env.CAI_ENV === 'staging') {
    return Environment.STAGING
  }

  if (process.env.CAI_ENV === 'development') {
    return Environment.DEVELOPMENT
  }

  return Environment.UNKNOWN
}

export const ENV = detectEnvironment()
export const isProduction = ENV === Environment.PRODUCTION
export const isStaging = ENV === Environment.STAGING
export const isDevelopment = ENV === Environment.DEVELOPMENT

console.info('ENV', ENV)

// PR previews build with CAI_ENV=staging but run on a per-PR subdomain
// (pr-<N>.preview.app.cai-staging.insure). They must behave as single-origin
// like dev — otherwise the cross-host redirects (useHostGuard) and appHref()
// links would bounce off the preview onto the real staging app.
export const isPreview =
  typeof window !== 'undefined' && window.location.hostname.endsWith('.preview.app.cai-staging.insure')

// Split-origin hosts per env. Marketing pages (/, /insurance, /privacy, /terms)
// are served from the marketing host; the product app from the app host. Used
// by the cross-host redirects in MarketingLayout / PrivateLayout. Empty in
// dev/preview (single origin) — the redirect helper treats empty as "no-op".
const useSplitOriginForStaging = isStaging && !isPreview
export const MARKETING_HOST = isProduction
  ? 'www.cai.ai'
  : useSplitOriginForStaging
    ? 'www.cai-staging.insure'
    : ''
const runtimeConfig = typeof window === 'undefined' ? undefined : window.__CAI_CONFIG__
const defaultAppHost = isProduction
  ? 'app.cai.insure'
  : useSplitOriginForStaging
    ? 'app.cai-staging.insure'
    : ''
export const APP_HOST = runtimeConfig?.insureAppOrigin
  ? new URL(runtimeConfig.insureAppOrigin).host
  : defaultAppHost

// Cai Bookkeeper, our other product. Origin only — its nav rows arrive as paths on it.
// The TLDs differ by environment: production is .ai, staging is .com. Verified by DNS,
// not inferred — neither bookkeeper.cai.com nor bookkeeper.cai-staging.ai exists.
// Locally the app serves it too: still absolute, so moving between products loads the page anew.
export const BOOKKEEPER_ORIGIN =
  runtimeConfig?.bookkeeperOrigin ||
  (isProduction
    ? 'https://bookkeeper.cai.ai'
    : isStaging
      ? 'https://bookkeeper.cai-staging.com'
      : typeof window === 'undefined'
        ? ''
        : window.location.origin)

export const bookkeeperHref = (path: string) =>
  `${BOOKKEEPER_ORIGIN}${path === '/' ? '/bookkeeper/login' : path}`

export const IS_SHARED_APP_HOST =
  typeof window !== 'undefined' &&
  Boolean(runtimeConfig?.appOrigin) &&
  runtimeConfig?.appOrigin === window.location.origin

/** Keep marketing pages off the shared app host, including client-side navigation. */
export function redirectsMarketingToApp(path: string): boolean {
  const normalized = path.replace(/\/$/, '')
  return (
    IS_SHARED_APP_HOST &&
    ([
      '',
      '/insurance',
      '/group-captive',
      '/how-we-use-ai',
      '/privacy',
      '/terms',
      '/cfo',
      '/insurance/large-fleet-coverage',
      '/insurance/amazon-relay',
      ...INSURANCE_VERTICAL_PATHS,
    ].includes(normalized) ||
      normalized === '/fuelapp' ||
      normalized.startsWith('/fuelapp/'))
  )
}

export const TERMINAL_PRE_BIND_PUBLIC_KEY = isProduction
  ? 'pk_prod_DV7CqVszKYwZd2WbHGRhu7FEFVYsw5nE'
  : 'pk_sandbox_D66cUk5hr6ZwcuPjDKfy9NepvKZM2LJb'
export const TERMINAL_POST_BIND_PUBLIC_KEY = isProduction
  ? 'pk_prod_F4Mb3vqCcrdfjm8UASFX3ZQ2Mes84nNe'
  : 'pk_sandbox_246uj6ZQY54xqkRUfLRk6aPWbfpXnhKw'

export const SENTRY_DSN =
  'https://f5977eb03f98707eb3c201d6fe13ebff@o4511387930853376.ingest.us.sentry.io/4511418068238336'

export const POSTHOG_HOST = 'https://us.i.posthog.com'
export const POSTHOG_APP_URL = 'https://us.posthog.com'

export const GOOGLE_MAPS_KEY = isProduction
  ? 'AIzaSyAXWq6LIadUKUVM1A0jE92J-FWdJ_hLBEk'
  : 'AIzaSyBsUuBjiMGZ5vU07-q-v-hqsI_P_Ljf2qg'

export const TEMPORAL_URL = isProduction
  ? 'https://temporal.cai-production.com'
  : isStaging
    ? 'https://temporal.cai-staging.com'
    : __CAI_TEMPORAL_ORIGIN__

export const ADMIN_BASE_URL = '/cl-admin'

// Django admin now 403s on the app host (INS-306) — relative in dev/preview, no console host there.
const ADMIN_CONSOLE_ORIGIN = isProduction
  ? 'https://console.cai.ai'
  : useSplitOriginForStaging
    ? 'https://console.cai-staging.ai'
    : ''

/** Build a link to Django admin — relative in dev/preview, absolute console host in staging/prod. */
export function adminHref(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${ADMIN_CONSOLE_ORIGIN}${normalized}`
}

/** Where customer-facing copy tells people to write when they need a human. */
export const SUPPORT_EMAIL = 'support@cai.ai'

// Dialpad Mini Dialer (Open CTI) client id, provisioned by Dialpad with our
// allowed origins registered. Public — it only ever appears in the embedded
// iframe URL (https://dialpad.com/apps/<id>). An empty string hides the dialer
// entirely (DialpadProvider reports status 'disabled').
export const DIALPAD_CLIENT_ID: string = '6rcEZKuLQzYzcHVULDuC2wQtg'

/**
 * The marketing pages live in this app during local development (one origin),
 * but in staging/production the marketing site is served from cai.ai while the
 * product app lives on its own host. So links that point *into the app*
 * (Login, "Get your quotes") must resolve to a same-origin relative path in
 * dev and to the absolute app host in staging/prod.
 */
const APP_BASE_URL =
  runtimeConfig?.insureAppOrigin ||
  (isProduction ? 'https://app.cai.insure' : useSplitOriginForStaging ? 'https://app.cai-staging.insure' : '')

function servedHere(origin: string): boolean {
  return !origin || (typeof window !== 'undefined' && origin === window.location.origin)
}

/** Insure's app is on this origin, so the product shell can open it in place; elsewhere it is a page load away. */
export const INSURE_IN_THIS_APP = servedHere(APP_BASE_URL)

/** Bookkeeper is on this origin, so the product shell can open it in place. */
export const BOOKKEEPER_IN_THIS_APP = servedHere(BOOKKEEPER_ORIGIN)

/** Build a link to an app route — relative in dev, absolute app host in staging/prod. */
export function appHref(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${APP_BASE_URL}${normalized}`
}

const MARKETING_BASE_URL = MARKETING_HOST ? `https://${MARKETING_HOST}` : ''

/**
 * Build a link to a marketing page — relative in dev, absolute marketing host in
 * staging/prod. A relative marketing path on the app host lands on the sign-in page.
 */
export function marketingHref(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${MARKETING_BASE_URL}${normalized}`
}
